import {
  MocChange,
  MocChangeVersion,
  MocStatusHistory,
  MocChangeSource,
  MocImpactAssessment,
  MocRiskSnapshot,
  MocApprovalWorkflow,
  MocImplementationPlan,
  MocImplementationStep,
  MocPostImplementationReview,
  MocClosureRecord,
  MocDashboardKPIs
} from './types';

const API_BASE = '/api/moc';

export const mocService = {
  // KPIs
  async getDashboardKPIs(orgId: string = 'ORG-XYZ'): Promise<MocDashboardKPIs> {
    const res = await fetch(`${API_BASE}/analytics/dashboard?organization_id=${orgId}`);
    if (!res.ok) throw new Error('MOC KPI verileri alinamadi');
    return res.json();
  },

  // Changes
  async getChanges(orgId: string = 'ORG-XYZ', filters?: {
    status?: string;
    department?: string;
    change_type?: string;
    search?: string;
  }): Promise<MocChange[]> {
    const params = new URLSearchParams({ organization_id: orgId });
    if (filters?.status) params.append('status', filters.status);
    if (filters?.department) params.append('department', filters.department);
    if (filters?.change_type) params.append('change_type', filters.change_type);
    if (filters?.search) params.append('search', filters.search);

    const res = await fetch(`${API_BASE}/changes?${params.toString()}`);
    if (!res.ok) throw new Error('Degisiklik kayitlari listelenemedi');
    return res.json();
  },

  async getChangeById(id: string, orgId: string = 'ORG-XYZ'): Promise<MocChange> {
    const res = await fetch(`${API_BASE}/changes/${id}?organization_id=${orgId}`);
    if (!res.ok) throw new Error('Degisiklik detayi alinamadi');
    return res.json();
  },

  async createChange(data: Partial<MocChange>, orgId: string = 'ORG-XYZ'): Promise<MocChange> {
    const res = await fetch(`${API_BASE}/changes`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'x-organization-id': orgId },
      body: JSON.stringify(data)
    });
    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      throw new Error(err.error || 'Degisiklik teklifi olusturulamadi');
    }
    return res.json();
  },

  async transitionStatus(
    id: string,
    newStatus: string,
    reason: string,
    changedById: string,
    changedByName: string,
    orgId: string = 'ORG-XYZ'
  ): Promise<MocChange> {
    const res = await fetch(`${API_BASE}/changes/${id}/transition`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'x-organization-id': orgId },
      body: JSON.stringify({
        new_status: newStatus,
        reason,
        changed_by_id: changedById,
        changed_by_name: changedByName
      })
    });
    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      throw new Error(err.error || 'Durum gecisi basarisiz');
    }
    return res.json();
  },

  // Versions & History
  async getVersions(mocId: string, orgId: string = 'ORG-XYZ'): Promise<MocChangeVersion[]> {
    const res = await fetch(`${API_BASE}/changes/${mocId}/versions?organization_id=${orgId}`);
    if (!res.ok) throw new Error('Versiyonlar alinamadi');
    return res.json();
  },

  async getStatusHistory(mocId: string, orgId: string = 'ORG-XYZ'): Promise<MocStatusHistory[]> {
    const res = await fetch(`${API_BASE}/changes/${mocId}/history?organization_id=${orgId}`);
    if (!res.ok) throw new Error('Tarihce alinamadi');
    return res.json();
  },

  // Sources
  async getSources(mocId: string, orgId: string = 'ORG-XYZ'): Promise<MocChangeSource[]> {
    const res = await fetch(`${API_BASE}/changes/${mocId}/sources?organization_id=${orgId}`);
    if (!res.ok) throw new Error('Degisiklik kaynaklari alinamadi');
    return res.json();
  },

  async addSource(mocId: string, source: Partial<MocChangeSource>, orgId: string = 'ORG-XYZ'): Promise<MocChangeSource> {
    const res = await fetch(`${API_BASE}/changes/${mocId}/sources`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'x-organization-id': orgId },
      body: JSON.stringify(source)
    });
    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      throw new Error(err.error || 'Kaynak eklenemedi');
    }
    return res.json();
  },

  // Impact Assessments
  async getImpacts(mocId: string, orgId: string = 'ORG-XYZ'): Promise<MocImpactAssessment[]> {
    const res = await fetch(`${API_BASE}/changes/${mocId}/impacts?organization_id=${orgId}`);
    if (!res.ok) throw new Error('Etki analizleri alinamadi');
    return res.json();
  },

  async addImpact(mocId: string, impact: Partial<MocImpactAssessment>, orgId: string = 'ORG-XYZ'): Promise<MocImpactAssessment> {
    const res = await fetch(`${API_BASE}/changes/${mocId}/impacts`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'x-organization-id': orgId },
      body: JSON.stringify(impact)
    });
    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      throw new Error(err.error || 'Etki analizi kaydedilemedi');
    }
    return res.json();
  },

  // Risks
  async getRisks(mocId: string, orgId: string = 'ORG-XYZ'): Promise<MocRiskSnapshot[]> {
    const res = await fetch(`${API_BASE}/changes/${mocId}/risks?organization_id=${orgId}`);
    if (!res.ok) throw new Error('Risk degerlendirmeleri alinamadi');
    return res.json();
  },

  async addRisk(mocId: string, risk: Partial<MocRiskSnapshot>, orgId: string = 'ORG-XYZ'): Promise<MocRiskSnapshot> {
    const res = await fetch(`${API_BASE}/changes/${mocId}/risks`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'x-organization-id': orgId },
      body: JSON.stringify(risk)
    });
    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      throw new Error(err.error || 'Risk kaydedilemedi');
    }
    return res.json();
  },

  // Approval
  async getApprovalWorkflow(mocId: string, orgId: string = 'ORG-XYZ'): Promise<MocApprovalWorkflow> {
    const res = await fetch(`${API_BASE}/changes/${mocId}/approval?organization_id=${orgId}`);
    if (!res.ok) throw new Error('Onay is akisi alinamadi');
    return res.json();
  },

  async initiateApproval(mocId: string, payload: any, orgId: string = 'ORG-XYZ'): Promise<MocApprovalWorkflow> {
    const res = await fetch(`${API_BASE}/changes/${mocId}/approval`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'x-organization-id': orgId },
      body: JSON.stringify(payload)
    });
    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      throw new Error(err.error || 'Onay baslatilamadi');
    }
    return res.json();
  },

  async recordApprovalDecision(mocId: string, decisionPayload: any, orgId: string = 'ORG-XYZ'): Promise<MocApprovalWorkflow> {
    const res = await fetch(`${API_BASE}/changes/${mocId}/approval/decision`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'x-organization-id': orgId },
      body: JSON.stringify(decisionPayload)
    });
    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      throw new Error(err.error || 'Onay karari verilemedi');
    }
    return res.json();
  },

  // Implementation
  async getImplementationPlan(mocId: string, orgId: string = 'ORG-XYZ'): Promise<MocImplementationPlan> {
    const res = await fetch(`${API_BASE}/changes/${mocId}/implementation?organization_id=${orgId}`);
    if (!res.ok) throw new Error('Uygulama plani alinamadi');
    return res.json();
  },

  async createImplementationPlan(mocId: string, plan: Partial<MocImplementationPlan>, orgId: string = 'ORG-XYZ'): Promise<MocImplementationPlan> {
    const res = await fetch(`${API_BASE}/changes/${mocId}/implementation`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'x-organization-id': orgId },
      body: JSON.stringify(plan)
    });
    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      throw new Error(err.error || 'Uygulama plani olusturulamadi');
    }
    return res.json();
  },

  async updateStepStatus(stepId: string, newStatus: string, evidence?: string, orgId: string = 'ORG-XYZ'): Promise<MocImplementationStep> {
    const res = await fetch(`${API_BASE}/implementation/steps/${stepId}/status`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'x-organization-id': orgId },
      body: JSON.stringify({ new_status: newStatus, completion_evidence_ref: evidence })
    });
    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      throw new Error(err.error || 'Adim durumu guncellenemedi');
    }
    return res.json();
  },

  // PIR
  async getPIR(mocId: string, orgId: string = 'ORG-XYZ'): Promise<MocPostImplementationReview> {
    const res = await fetch(`${API_BASE}/changes/${mocId}/pir?organization_id=${orgId}`);
    if (!res.ok) throw new Error('PIR incelemesi alinamadi');
    return res.json();
  },

  async recordPIR(mocId: string, pir: Partial<MocPostImplementationReview>, orgId: string = 'ORG-XYZ'): Promise<MocPostImplementationReview> {
    const res = await fetch(`${API_BASE}/changes/${mocId}/pir`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'x-organization-id': orgId },
      body: JSON.stringify(pir)
    });
    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      throw new Error(err.error || 'PIR kaydedilemedi');
    }
    return res.json();
  },

  // Closure
  async getClosure(mocId: string, orgId: string = 'ORG-XYZ'): Promise<MocClosureRecord> {
    const res = await fetch(`${API_BASE}/changes/${mocId}/closure?organization_id=${orgId}`);
    if (!res.ok) throw new Error('Kapanis kaydi alinamadi');
    return res.json();
  },

  async closeChange(mocId: string, payload: any, orgId: string = 'ORG-XYZ'): Promise<MocClosureRecord> {
    const res = await fetch(`${API_BASE}/changes/${mocId}/closure`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'x-organization-id': orgId },
      body: JSON.stringify(payload)
    });
    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      throw new Error(err.error || 'Kapanis gerceklestirilemedi');
    }
    return res.json();
  }
};
